import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type CollectionResponse, type ResourceResponse } from '@shared/api/client';
import type { Concepto, ConceptoFormData } from '../types/concepto';

const BASE = '/billing/concepts';

export const conceptoKeys = {
  all: ['conceptos'] as const,
  list: (emisorId?: number, clienteId?: number) =>
    [...conceptoKeys.all, 'list', emisorId ?? 0, clienteId ?? 0] as const,
  detail: (id: number) => [...conceptoKeys.all, 'detail', id] as const,
};

export function useConceptos(emisorId?: number, clienteId?: number) {
  return useQuery({
    queryKey: conceptoKeys.list(emisorId, clienteId),
    queryFn: async () => {
      const { data } = await api.get<CollectionResponse<Concepto>>(BASE, {
        params: { emisor_id: emisorId, cliente_id: clienteId },
      });
      return data.data;
    },
  });
}

export function useCreateConcepto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ConceptoFormData) => {
      const { data } = await api.post<ResourceResponse<Concepto>>(BASE, payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: conceptoKeys.all }),
  });
}

export function useUpdateConcepto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<ConceptoFormData> }) => {
      const { data } = await api.put<ResourceResponse<Concepto>>(`${BASE}/${id}`, payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: conceptoKeys.all }),
  });
}

export function useDeleteConcepto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`${BASE}/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: conceptoKeys.all }),
  });
}
